import { FC, FormEvent, useContext, useEffect, useState } from "react";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import appStyles from "../../../App.module.css";
import { formatAmount } from "../../../app-constants";
import amtConstants from "../../config/amt-constants";
import Stepper from "../Stepper";
import { ISingleTripQuoteContext, SingleTripQuoteContext } from "../TripQuote";
import QuoteApi from "../api/quote";
import Footer from "../common/Footer";
import OrderSummary from "../common/OrderSummary";
import BackArrow from "../icons/BackArrow";
import ForwardArrow from "../icons/ForwardArrow";

interface PaymentFormProps {}

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(amtConstants.publishableKey!);

const PaymentForm: FC<PaymentFormProps> = (props: PaymentFormProps) => {
  const { order } = useContext(SingleTripQuoteContext) as ISingleTripQuoteContext;

  const [loading, setLoading] = useState<boolean>(false);
  const [stripeIntentData, setStripeIntentData] = useState<any>(null);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const response: any = await QuoteApi.getPaymentIntent(order.order_number);
      // console.log(response.data);
      setStripeIntentData(response.data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className={appStyles.bfSpinnerOverlay}>
          <div className={appStyles.bfSpinner}></div>
        </div>
      )}

      <div className={`${appStyles.mainContainer} ${appStyles.W600}`}>
        <div className={`${appStyles.quotePaymentWrapper}`}>
          <div className={appStyles.headerWrapper}>
            <div className={appStyles.pageTitle}>
              <h1 className={`${appStyles.colorPrimary}`}>Make Payment</h1>
            </div>
          </div>
          <Stepper />

          <OrderSummary />

          <div className={`${appStyles.formBlock} ${appStyles.bfMarginTop}`}>
            {!loading && stripeIntentData && (
              <Elements stripe={stripePromise} options={{ clientSecret: stripeIntentData.client_secret }}>
                <PaymentFormElement />
              </Elements>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default PaymentForm;

type FormProps = {};

const PaymentFormElement: FC<FormProps> = (props: FormProps) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { next, prev, quoteResult, quoteData } = useContext(SingleTripQuoteContext) as ISingleTripQuoteContext;

  const stripe = useStripe();
  const elements = useElements();

  const handlePaymentSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsProcessingPayment(true);

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: "",
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unexpected error occurred.");
      }
      setIsProcessingPayment(false);
      return;
    } else {
      setIsProcessingPayment(false);
      next();
    }
  };

  return (
    <form onSubmit={handlePaymentSubmit}>
      <div className={`${appStyles.borderBox} ${appStyles.formBox}`}>
        <div className={appStyles.formWrapper}>
          <div className={appStyles.pageSubTitle}>
            <h2 className={appStyles.colorPrimary}>Card information</h2>
          </div>
          <div className={`${appStyles.formRow} ${appStyles.paymentCardWrapper}`}>
            <PaymentElement
              onReady={() => {
                setIsStripeLoading(false);
              }}
            />
          </div>
        </div>
      </div>

      {!isStripeLoading && (
        <div className={`${appStyles.paymentButtonWrapper} ${appStyles.formRow}`}>
          <div className={`${appStyles.buttonBlock} ${appStyles.buttonBlockNextPrev}`}>
            <button
              className={`${appStyles.bfButton} ${appStyles.bfButtonSecondary} ${appStyles.bfButtonOutline} ${appStyles.bfbuttonHasIcon} ${appStyles.bfbuttonIconBefore}`}
              type="button"
              onClick={() => {
                prev();
              }}
            >
              Back
              <span className={`${appStyles.bfButtonIcon} ${appStyles.bfButtonIconPrev}`}>
                <BackArrow />
              </span>
            </button>
            <button
              className={`${appStyles.bfButton} ${appStyles.bfbuttonHasIcon} ${appStyles.bfbuttonIconAfter} ${appStyles.bfbuttonLong}`}
              type="submit"
              disabled={isProcessingPayment || !stripe || !elements}
            >
              {isProcessingPayment
                ? "Processing.."
                : `Make Payment ${formatAmount(
                    quoteResult?.quoted_price || 0,
                    quoteData.host_country?.code || "US",
                    quoteData.currency?.value || "GBP"
                  )}`}
              <span className={`${appStyles.bfButtonIcon} ${appStyles.bfButtonIconNext}`}>
                <ForwardArrow />
              </span>
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className={appStyles.formRow}>
          <div className={appStyles.battlefaceServerError}>{errorMessage}</div>
        </div>
      )}
    </form>
  );
};
